import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertTriangle, LogOut, Bell, ChevronRight, UserRound } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useFieldProfile } from '../../hooks/useFieldProfile';
// A cert expiry and a clock-history day are calendar days, and both used to
// finish on a bare toLocaleDateString(), so the hand's own phone picked the
// order of the numbers. formatCalendarDate keeps the local parse and pins a
// month name.
import { daysUntilCalendarDate, formatCalendarDate } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-center justify-between p-3.5">
    <span className="text-xs text-fieldiq-text">{label}</span>
    <span className="text-sm font-medium">{value}</span>
  </div>
);

/*
 * A CERT'S STATUS COMES FROM ITS EXPIRY DATE (fix/field-home-trucking-rentals).
 * GET /field/profile sends `{ name, expiresAt }` and no status, and this row
 * read `ok !== false`, so a missing flag meant "valid": Tyler Brennan (Apex
 * Surface Rentals) saw green checks on an H2S Alive that lapsed 2026-08-15 and
 * an Air Brake that lapsed 2026-09-30. An absent flag is not a clean bill.
 * Calendar days against the TENANT's today, the same rule and 60-day window as
 * the trucking Drivers register; no date at all is its own slate state, never
 * valid. An `ok: false` the API does send still wins.
 */
type CredStatus = 'valid' | 'expiring' | 'expired' | 'unknown';
const CRED_BADGE: Record<CredStatus, { label: string; fill: string }> = {
  valid: { label: 'Current', fill: 'bg-fi-signal-green' },
  expiring: { label: 'Renew soon', fill: 'bg-fi-signal-amber' },
  expired: { label: 'Expired', fill: 'bg-fi-signal-red' },
  unknown: { label: 'No expiry on file', fill: 'bg-fi-signal-slate' },
};
export function credStatus(expires: string | undefined, ok: boolean | undefined, timeZone: string | null): CredStatus {
  const days = daysUntilCalendarDate(expires ?? null, timeZone);
  if (days != null && days < 0) return 'expired';
  if (ok === false) return 'expired';
  if (days == null) return 'unknown';
  if (days <= 60) return 'expiring';
  return 'valid';
}

const CredRow = ({ label, expires, status }: { label: string; expires?: string; status: CredStatus }) => {
  const badge = CRED_BADGE[status];
  const good = status === 'valid';
  return (
    <div data-testid="cred-row" className="flex items-center justify-between gap-3 p-3.5">
      <div className="flex min-w-0 items-center gap-2.5">
        {good ? (
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" />
        ) : (
          <AlertTriangle className="h-4 w-4 shrink-0 text-fieldiq-text" aria-hidden="true" />
        )}
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span className={`rounded-full border border-black/20 px-2 py-0.5 text-[10px] font-black uppercase text-fi-signal-ink ${badge.fill}`}>
          {badge.label}
        </span>
        {expires && (
          <span className="text-xs text-fieldiq-text">
            Exp {formatCalendarDate(expires, { month: 'short', year: 'numeric' }) ?? '—'}
          </span>
        )}
      </div>
    </div>
  );
};

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { data: profile, isLoading } = useFieldProfile();
  const timeZone = useTenantTimeZone();

  const knownName =
    profile?.name ??
    ([user?.first_name || user?.firstName, user?.last_name || user?.lastName]
      .filter(Boolean)
      .join(' ') || null);
  const displayName = knownName || 'Field Worker';

  /*
   * From the name the page shows. This added two `[0]?.toUpperCase()` reads
   * of first/last fields /auth/me does not send; undefined + undefined is NaN,
   * so every field login read "??" beside their own name
   * (fix/field-home-trucking-rentals). No name draws a person icon.
   */
  const parts = (knownName ?? '').trim().split(/\s+/).filter(Boolean);
  const initials = parts.length === 0 ? null : (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();

  const email = profile?.email ?? user?.email ?? '';
  const positions: string[] = profile?.positions ?? [];
  const payRate = profile?.payRateSummary ?? null;
  const certifications = profile?.certifications ?? [];
  const clockHistory = profile?.recentClockHistory ?? [];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="px-4 pt-4 pb-4">
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 size={28} className="animate-spin text-fieldiq-text" />
        </div>
      ) : (
        <>
          {/* Avatar + name */}
          <div className="flex items-center gap-3">
            <div data-testid="profile-avatar" className="h-14 w-14 rounded-full bg-fieldiq-accent/20 text-fieldiq-accent grid place-items-center text-base font-bold">
              {initials ?? <UserRound className="h-6 w-6" aria-hidden="true" />}
            </div>
            <div>
              <h2 className="text-lg font-semibold">{displayName}</h2>
              <p className="text-xs text-fieldiq-text">{email}</p>
            </div>
          </div>

          {/* Position & pay */}
          {(positions.length > 0 || payRate) && (
            <section className="mt-5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-fieldiq-text mb-2">
                Position & pay
              </p>
              <div className="rounded-xl border border-fieldiq-border bg-fieldiq-card divide-y divide-fieldiq-border">
                {positions.length > 0 && <Row label="Position(s)" value={positions.join(', ')} />}
                {payRate && <Row label="Pay rate" value={payRate} />}
              </div>
            </section>
          )}

          {/* Certifications */}
          {certifications.length > 0 && (
            <section className="mt-5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-fieldiq-text mb-2">
                Licenses & Certifications
              </p>
              <div className="rounded-xl border border-fieldiq-border bg-fieldiq-card divide-y divide-fieldiq-border">
                {certifications.map((c) => (
                  <CredRow
                    key={c.name}
                    label={c.name}
                    expires={c.expiresAt}
                    status={credStatus(c.expiresAt, c.ok, timeZone)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Activity */}
          {clockHistory.length > 0 && (
            <section className="mt-5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-fieldiq-text mb-2">
                Recent clock history
              </p>
              <div className="rounded-xl border border-fieldiq-border bg-fieldiq-card divide-y divide-fieldiq-border">
                {clockHistory.map((entry, i) => (
                  <div key={i} className="flex items-center justify-between p-3.5">
                    <div>
                      <p className="text-sm font-medium">
                        {formatCalendarDate(entry.date, {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                        }) ?? '—'}
                      </p>
                      <p className="text-[11px] text-fieldiq-text">
                        {entry.clockIn}
                        {entry.clockOut ? ` — ${entry.clockOut}` : ' (active)'}
                      </p>
                    </div>
                    {entry.hours != null && (
                      <span className="text-sm text-fieldiq-text font-mono">
                        {entry.hours.toFixed(1)}h
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Settings */}
          <section className="mt-5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-fieldiq-text mb-2">
              Settings
            </p>
            <div className="rounded-xl border border-fieldiq-border bg-fieldiq-card divide-y divide-fieldiq-border">
              <button
                onClick={() => navigate('/field/settings/notifications')}
                className="w-full flex items-center justify-between p-3.5"
              >
                <div className="flex items-center gap-2.5">
                  <Bell className="h-4 w-4 text-purple-400" />
                  <span className="text-sm font-medium">Notifications</span>
                </div>
                <ChevronRight className="h-4 w-4 text-fieldiq-muted" />
              </button>
            </div>
          </section>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="mt-6 w-full h-11 rounded-lg border border-fieldiq-border flex items-center justify-center gap-2 text-sm font-medium text-red-400 hover:bg-red-600/10 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </>
      )}
    </div>
  );
}
